// admin/src/pages/GatewayMonitoring/components/RenameGatewayDialog.js
'use strict';

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { updateGateway, extractGatewayErrorMessage } from '../gatewayApi';

export default function RenameGatewayDialog({
  open = false,
  gateway = null,
  onClose,
  onSuccess,
}) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (gateway) {
      setName(gateway.name || '');
    }
    setError(null);
  }, [open, gateway]);

  if (!gateway) return null;

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Gateway name cannot be empty.');
      return;
    }
    if (trimmed.length > 128) {
      setError('Gateway name must be 128 characters or fewer.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateGateway(gateway._id, { name: trimmed });
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      setError(extractGatewayErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      data-testid="rename-gateway-dialog"
    >
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        <EditIcon color="primary" />
        Rename Gateway
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Gateway Name"
          value={name}
          disabled={saving}
          data-testid="rename-gateway-input"
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 128 }}
          sx={{ mt: 1 }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving || !name.trim()}
          data-testid="rename-gateway-save-btn"
          onClick={handleSave}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
